import {
	ComponentProps,
	HTMLAttributes,
	ReactNode,
	Ref,
	createContext,
	forwardRef,
	useContext,
} from "react";
import { To } from "react-router-dom";
import classNames from "classnames/bind";
import { CheckIcon } from "app/icons/navigation";
import { Link } from "app/components/ui/Link/Link";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import styles from "./Stepper.module.scss";

const cx = classNames.bind(styles);

export const STEP_STATUS = {
	COMPLETED: "completed",
	ACTIVE: "active",
	UPCOMING: "upcoming",
} as const;

export type StepStatus = (typeof STEP_STATUS)[keyof typeof STEP_STATUS];

type StepperContextValue = {
	value: number;
	clickable: boolean;
};

const StepperContext = createContext<StepperContextValue | null>(null);

/** Reads the root Stepper context. Throws when used outside a <Stepper>. */
const useStepperContext = (): StepperContextValue => {
	const context = useContext(StepperContext);

	if (context === null) {
		throw new Error("Stepper components must be used within a <Stepper>.");
	}

	return context;
};

type StepperItemContextValue = {
	step: number;
	status: StepStatus;
	isLink: boolean;
	to?: To;
};

const StepperItemContext = createContext<StepperItemContextValue | null>(null);

/** Reads the current StepperItem context. Throws when used outside a <StepperItem>. */
const useStepperItemContext = (): StepperItemContextValue => {
	const context = useContext(StepperItemContext);

	if (context === null) {
		throw new Error("Stepper item parts must be used within a <StepperItem>.");
	}

	return context;
};

export type StepperProps = HTMLAttributes<HTMLDivElement> & {
	/** Zero-based index of the active step. Earlier steps are completed, later ones upcoming. */
	value: number;
	/** When true, completed steps holding a `to` render as links. */
	clickable?: boolean;
};

/**
 * Root of the stepper. Holds the active step index and the clickable mode,
 * and exposes them to every part through context.
 */
export const Stepper = forwardRef<HTMLDivElement, StepperProps>(
	({ value, clickable = false, className, children, ...props }, ref) => {
		return (
			<StepperContext.Provider value={{ value, clickable }}>
				<div ref={ref} className={cx("stepper", className)} {...props}>
					{children}
				</div>
			</StepperContext.Provider>
		);
	}
);
Stepper.displayName = "Stepper";

export type StepperNavProps = HTMLAttributes<HTMLOListElement> & {
	children?: ReactNode;
};

/**
 * Ordered list of steps. Wraps the list in a `<nav>` landmark when the stepper
 * is clickable (it becomes a navigation region); renders a bare `<ol>` otherwise.
 * Pass an `aria-label` to name the navigation landmark.
 */
export const StepperNav = forwardRef<HTMLOListElement, StepperNavProps>(
	({ className, children, "aria-label": ariaLabel, ...props }, ref) => {
		const { clickable } = useStepperContext();

		const list = (
			<ol
				ref={ref}
				className={cx("stepper__list", className)}
				aria-label={clickable ? undefined : ariaLabel}
				{...props}
			>
				{children}
			</ol>
		);

		if (clickable) {
			return (
				<nav className={cx("stepper__nav")} aria-label={ariaLabel}>
					{list}
				</nav>
			);
		}

		return list;
	}
);
StepperNav.displayName = "StepperNav";

export type StepperItemProps = HTMLAttributes<HTMLLIElement> & {
	/** Zero-based index of this step. */
	step: number;
	/**
	 * Destination for a completed, clickable step. Accepts a full location, so
	 * pass the pathname **and** search the step was reached with (e.g.
	 * `"/options?productId=123"` or `{ pathname, search }`) to preserve its params.
	 */
	to?: To;
	children?: ReactNode;
};

/** Derives a step status from its index and the currently active index. */
const getStepStatus = (step: number, value: number): StepStatus => {
	if (step < value) {
		return STEP_STATUS.COMPLETED;
	}

	if (step === value) {
		return STEP_STATUS.ACTIVE;
	}

	return STEP_STATUS.UPCOMING;
};

/**
 * A single step. Computes its status from its index against the active value and
 * shares it with the indicator, title, trigger and separator through context.
 */
export const StepperItem = forwardRef<HTMLLIElement, StepperItemProps>(
	({ step, to, className, children, ...props }, ref) => {
		const { value, clickable } = useStepperContext();

		const status = getStepStatus(step, value);
		const isLink = clickable && status === STEP_STATUS.COMPLETED && to !== undefined;

		return (
			<StepperItemContext.Provider value={{ step, status, isLink, to }}>
				<li
					ref={ref}
					className={cx("stepper__item", className)}
					data-status={status}
					{...props}
				>
					{children}
				</li>
			</StepperItemContext.Provider>
		);
	}
);
StepperItem.displayName = "StepperItem";

export type StepperTriggerProps = HTMLAttributes<HTMLElement> & {
	children?: ReactNode;
};

/**
 * Wraps the indicator and title. Renders a router link for a completed clickable
 * step, and a plain span otherwise.
 */
export const StepperTrigger = forwardRef<HTMLElement, StepperTriggerProps>(
	({ className, children, ...props }, ref) => {
		const { isLink, to } = useStepperItemContext();

		if (isLink && to !== undefined) {
			return (
				<Link
					ref={ref as Ref<HTMLAnchorElement>}
					to={to}
					className={cx("stepper__trigger", "stepper__trigger--link", className)}
					{...props}
				>
					{children}
				</Link>
			);
		}
		return (
			<span
				ref={ref as Ref<HTMLSpanElement>}
				className={cx("stepper__trigger", className)}
				{...props}
			>
				{children}
			</span>
		);
	}
);
StepperTrigger.displayName = "StepperTrigger";

export type StepperIndicatorProps = HTMLAttributes<HTMLSpanElement> & {
	children?: ReactNode;
};

/**
 * The step circle. Shows a check for a completed step, or its `children`
 * (e.g. a number) for active and upcoming steps. Decorative for assistive tech.
 */
export const StepperIndicator = forwardRef<HTMLSpanElement, StepperIndicatorProps>(
	({ className, children, ...props }, ref) => {
		const { status } = useStepperItemContext();

		return (
			<span
				ref={ref}
				className={cx("stepper__indicator", className)}
				data-status={status}
				aria-hidden="true"
				{...props}
			>
				{status === STEP_STATUS.COMPLETED ? (
					<CheckIcon className={cx("stepper__check")} />
				) : (
					children
				)}
			</span>
		);
	}
);
StepperIndicator.displayName = "StepperIndicator";

export type StepperTitleProps = ComponentProps<typeof Typography>;

/** The step label. Emphasised on the active step, which carries `aria-current`. */
export const StepperTitle = forwardRef<HTMLSpanElement, StepperTitleProps>(
	({ className, children, ...props }, ref) => {
		const { status } = useStepperItemContext();

		const isActive = status === STEP_STATUS.ACTIVE;

		return (
			<Typography
				ref={ref}
				component="span"
				variant={TYPOGRAPHY_VARIANTS.SMALL}
				isBold={isActive}
				className={cx("stepper__title", className)}
				data-status={status}
				aria-current={isActive ? "step" : undefined}
				{...props}
			>
				{children}
			</Typography>
		);
	}
);
StepperTitle.displayName = "StepperTitle";

export type StepperSeparatorProps = HTMLAttributes<HTMLSpanElement>;

/**
 * The connector drawn after a step. Filled when the preceding step is completed.
 * Omit it on the last item. Decorative for assistive tech.
 */
export const StepperSeparator = forwardRef<HTMLSpanElement, StepperSeparatorProps>(
	({ className, ...props }, ref) => {
		const { status } = useStepperItemContext();

		return (
			<span
				ref={ref}
				className={cx("stepper__separator", className)}
				data-status={status}
				aria-hidden="true"
				{...props}
			/>
		);
	}
);
StepperSeparator.displayName = "StepperSeparator";
